import { motion, AnimationProps } from 'framer-motion';
import { ReactNode, CSSProperties } from 'react';
import { getColumnClassName } from './columnWidthStyles';
import { NewTableColumn } from './types';
import { tw } from 'utils/tailwindUtils';
import { useThemeContext } from 'shared/context/themeContext';

interface Props<T> {
  row: T;
  index: number;
  columns: NewTableColumn<T>[];
  tableId: string;
  onRowClick?: (row: T) => void;
  renderRowActions?: (row: T) => ReactNode;
  rowClassName?: string;
  initialAnimation?: AnimationProps['initial'];
  animateAnimation?: AnimationProps['animate'];
  transitionAnimation?: AnimationProps['transition'];
  customStyle?: CSSProperties; // <--- Añadido para soportar la animación CSS nativa
}

const alignClass = (align?: 'start' | 'end' | 'center') =>
  align === 'end'
    ? 'justify-end text-right'
    : align === 'center'
    ? 'justify-center text-center'
    : '';

function NewTableRow<T>({
  row,
  index,
  columns,
  tableId,
  onRowClick,
  renderRowActions,
  rowClassName,
  initialAnimation = { opacity: 0, y: 8 },
  animateAnimation = { opacity: 1, y: 0 },
  transitionAnimation = { delay: index * 0.05, duration: 0.2 },
  customStyle
}: Props<T>) {
  const { theme } = useThemeContext();
  const isDark = theme === 'dark';

  return (
    <motion.div
      initial={initialAnimation}
      animate={animateAnimation}
      transition={transitionAnimation}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      style={customStyle}
      className={tw(
        'group dark:hover:bg-darkLightHover relative flex w-max min-w-full items-center gap-4 rounded-2xl border border-secondary-200 bg-whiteLight hover:bg-white px-6 py-4 shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-darkLight',
        onRowClick ? 'cursor-pointer' : '',
        rowClassName
      )}
    >
      <div className="flex flex-1 min-w-0 items-center justify-between gap-4">
        {columns.map((column) => {
          const content = column.render(row, index);
          const isStickyRight = column.sticky === 'right';
          return (
            <div
              key={column.key}
              className={tw(
                'min-w-0 flex shrink-0',
                getColumnClassName(tableId, column.key),
                alignClass(column.align),
                isStickyRight &&
                  `sticky right-0 z-10 ${
                    isDark
                      ? 'group-hover:bg-darkLightHover bg-darkLight'
                      : 'bg-whiteLight group-hover:bg-white'
                  }`
              )}
            >
              {column.truncate ? (
                <div className="truncate">{content}</div>
              ) : (
                content
              )}
            </div>
          );
        })}
      </div>
      {renderRowActions && (
        <div
          className={tw(
            'flex shrink-0 justify-end',
            getColumnClassName(tableId, 'actions')
          )}
        >
          {renderRowActions(row)}
        </div>
      )}
    </motion.div>
  );
}

export default NewTableRow;
